import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);
  const itemRefs = useRef([]);
  const ctaRef = useRef(null);

  useEffect(() => {
    const headerObs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisibleItems(p => [...new Set([...p, 'header'])]); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) headerObs.observe(sectionRef.current);

    const itemObs = itemRefs.current.map((ref, i) => {
      const obs = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) setTimeout(() => setVisibleItems(p => [...new Set([...p, i])]), i * 100);
        },
        { threshold: 0.1 }
      );
      if (ref) obs.observe(ref);
      return obs;
    });

    const ctaObs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisibleItems(p => [...new Set([...p, 'cta'])]); },
      { threshold: 0.1 }
    );
    if (ctaRef.current) ctaObs.observe(ctaRef.current);

    return () => { headerObs.disconnect(); itemObs.forEach(o => o.disconnect()); ctaObs.disconnect(); };
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "How much does it cost to work with Eshwald Ops?",
      answer: "We work on a revenue-share model, which means no upfront costs. We only succeed when you succeed. The exact terms depend on your specific situation and goals, which we'll discuss on our discovery call."
    },
    {
      question: "How long does it take to see results?",
      answer: "Most creators see their first digital product launched within 60-90 days. Revenue growth varies, but our partners typically see significant increases within 3-6 months as we optimize and scale."
    },
    {
      question: "Do I need a large following to work with you?",
      answer: "We typically work with creators who have at least 10,000 engaged followers. However, engagement quality matters more than follower count. If you have a highly engaged niche audience, we'd love to talk."
    },
    {
      question: "What if I already have digital products?",
      answer: "No worries! We can create new offerings or even refactor your existing products for better positioning. Many of our most successful partnerships are with creators who already have some products but want to scale."
    },
    {
      question: "How involved do I need to be in the process?",
      answer: "You'll be involved in strategic decisions and content creation, but we handle the heavy lifting. Expect to spend 5-10 hours per week on partnership activities, mostly focused on what you do best: creating content."
    },
    {
      question: "What makes Eshwald Ops different from other agencies?",
      answer: "We're not an agency—we're partners. We invest our time, expertise, and resources upfront because we believe in your potential. Plus, our revenue-share model means we're incentivized to maximize your success, not just bill hours."
    }
  ];

  return (
    <section id="faq" ref={sectionRef} className="py-24 px-6 bg-[rgb(26,28,30)] relative overflow-hidden">
      <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[rgba(124,56,178,0.05)] blur-[120px] rounded-full pointer-events-none"></div>

      <div className="max-w-[900px] mx-auto relative z-10">
        <div className={`text-center mb-16 transition-all duration-700 ${
          visibleItems.includes('header') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
        }`}>
          <div className="inline-flex items-center space-x-2 text-[rgb(197,155,47)] text-sm font-semibold mb-4">
            <div className="w-8 h-[2px] bg-gradient-to-r from-transparent to-[rgb(197,155,47)]"></div>
            <span>FAQ</span>
            <div className="w-8 h-[2px] bg-gradient-to-l from-transparent to-[rgb(197,155,47)]"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Frequently Asked <span className="bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text">Questions</span>
          </h2>
          <p className="text-xl text-[rgb(218,218,218)]">
            Everything you need to know about partnering with us
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              ref={el => (itemRefs.current[index] = el)}
              className={`bg-[rgb(38,40,42)] border rounded-xl overflow-hidden transition-all duration-500 ${
                openIndex === index
                  ? 'border-[rgb(197,155,47)] shadow-[0_4px_24px_rgba(197,155,47,0.15)]'
                  : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(197,155,47,0.5)]'
              } ${
                visibleItems.includes(index)
                  ? 'opacity-100 translate-y-0 scale-100'
                  : 'opacity-0 translate-y-6 scale-[0.97]'
              }`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left transition-colors duration-200 group"
              >
                <span className={`text-lg font-semibold pr-4 transition-colors duration-200 ${
                  openIndex === index ? 'text-[rgb(197,155,47)]' : 'text-white group-hover:text-[rgb(224,194,95)]'
                }`}>
                  {faq.question}
                </span>
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                  openIndex === index
                    ? 'bg-[rgb(197,155,47)] rotate-180'
                    : 'bg-[rgba(197,155,47,0.15)] rotate-0 group-hover:bg-[rgba(197,155,47,0.3)]'
                }`}>
                  <ChevronDown
                    size={18}
                    className={`transition-colors duration-300 ${
                      openIndex === index ? 'text-[rgb(17,17,19)]' : 'text-[rgb(197,155,47)]'
                    }`}
                  />
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6 pt-0">
                  <div className="h-[1px] bg-gradient-to-r from-transparent via-[rgba(197,155,47,0.4)] to-transparent mb-4"></div>
                  <p className="text-[rgb(218,218,218)] leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div ref={ctaRef} className={`mt-12 text-center transition-all duration-700 ${
          visibleItems.includes('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-lg text-[rgb(218,218,218)] mb-4">
            Still have questions?
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-transparent border-2 border-[rgb(197,155,47)] text-white px-8 py-3 rounded-xl font-semibold hover:bg-[rgba(197,155,47,0.1)] hover:shadow-[0_0_20px_rgba(197,155,47,0.3)] transition-all duration-300 hover:scale-105"
          >
            Get in Touch
          </button>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
